/**
 * PATH: apps/qa/src/pages/report-editor/OverrideHistory.tsx
 * FILE_ID: QA-REPORT-OVERRIDE-HISTORY-001
 * PURPOSE: Display immutable audited section override records.
 */

import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { reportDraftApi } from '@/lib/api'

export function OverrideHistory({ draftId, sectionType }: { draftId: string; sectionType: string }) {
  const history = useQuery({
    queryKey: ['qa-report-overrides', draftId, sectionType],
    queryFn: () => reportDraftApi.getOverrides(draftId, sectionType),
  })
  if (history.isLoading) return <p className="mx-auto mb-3 flex max-w-4xl items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading immutable override history…</p>
  if (history.isError) return <p role="alert" className="mx-auto mb-3 max-w-4xl rounded-lg bg-destructive/10 p-3 text-sm text-destructive">Override history is unavailable. The backend override contract may not be deployed yet.</p>
  if (!history.data?.length) return <p className="mx-auto mb-3 max-w-4xl text-xs text-muted-foreground">No audited overrides recorded for this section.</p>
  return (
    <details className="mx-auto mb-3 max-w-4xl rounded-lg border border-border bg-background p-3">
      <summary className="cursor-pointer text-sm font-medium">Immutable override history ({history.data.length})</summary>
      <ol className="mt-3 space-y-3">
        {history.data.map((entry) => <li key={entry.id} className="border-l-2 border-amber-400 pl-3 text-xs">
          <p className="font-medium text-foreground">{entry.actor_name || 'Authorized reviewer'} · {new Date(entry.created_at).toLocaleString()}</p>
          <p className="mt-1 text-muted-foreground">Reason: {entry.reason}</p>
          <details className="mt-1"><summary className="cursor-pointer text-muted-foreground">View original and replacement</summary><div className="mt-2 grid gap-2 md:grid-cols-2"><pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted p-2">{formatOverrideValue(entry.original_value)}</pre><pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted p-2">{formatOverrideValue(entry.current_value)}</pre></div></details>
        </li>)}
      </ol>
    </details>
  )
}

function formatOverrideValue(value: unknown) { return typeof value === 'string' ? value : JSON.stringify(value, null, 2) }
